import { LegalLayout } from "@/components/site/LegalLayout";

const LAST_UPDATED = "July 14, 2026";

const intro = [
  {
    type: "p",
    text: "This Cookie Policy explains how the World Financial Innovation Institute (\"WFII\", \"we\", \"us\", or \"our\") uses cookies and similar technologies on our website. It should be read together with our Privacy Policy, which describes more broadly how we handle personal information.",
  },
];

const sections = [
  {
    id: "what-are-cookies",
    title: "What Are Cookies",
    body: [
      {
        type: "p",
        text: "Cookies are small text files that a website places on your computer or mobile device when you visit. They are widely used to make websites work, to make them work more efficiently, and to provide information to site owners. Similar technologies include local storage, pixels, and tags; in this Policy, we refer to all of these collectively as \"cookies\".",
      },
      {
        type: "p",
        text: "Cookies set by the website you are visiting are called \"first-party cookies\". Cookies set by other organizations through the website — for example, analytics providers — are called \"third-party cookies\".",
      },
    ],
  },
  {
    id: "how-we-use-cookies",
    title: "How We Use Cookies",
    body: [
      { type: "p", text: "We use cookies to:" },
      {
        type: "ul",
        items: [
          "Operate the website and enable core functionality, such as page navigation and security.",
          "Remember your preferences, such as language or display settings.",
          "Understand how visitors use the website — which pages are visited, how long visits last, and how visitors arrive — so we can improve content and structure.",
          "Measure the effectiveness of our educational content, resources, and communications.",
        ],
      },
    ],
  },
  {
    id: "types-of-cookies",
    title: "Types of Cookies We Use",
    body: [
      {
        type: "ul",
        items: [
          "Strictly necessary cookies — required for the website to function and cannot be switched off in our systems. They are usually set in response to actions you take, such as setting preferences or filling in forms. You can set your browser to block these cookies, but some parts of the website may not work as a result.",
          "Performance and analytics cookies — allow us to count visits and traffic sources so we can measure and improve site performance. These cookies help us understand which pages are the most and least popular and how visitors move around the site. The information collected is aggregated and does not directly identify you.",
          "Functional cookies — enable enhanced functionality and personalization, such as remembering choices you have made. If you do not allow these cookies, some or all of these features may not function properly.",
          "Marketing and communication cookies — may be used to measure engagement with our newsletters, event invitations, and educational campaigns, and to limit how often you see the same message. We use these sparingly and in a manner consistent with our institutional mandate.",
        ],
      },
    ],
  },
  {
    id: "third-party-cookies",
    title: "Third-Party Cookies",
    body: [
      {
        type: "p",
        text: "Some cookies on our website may be set by third-party services we use, such as analytics providers, video hosting platforms, or event registration tools. These third parties may process information about your visit in accordance with their own privacy and cookie policies. We encourage you to review the policies of those providers. We do not control third-party cookies and are not responsible for their practices.",
      },
    ],
  },
  {
    id: "cookie-duration",
    title: "How Long Cookies Stay on Your Device",
    body: [
      {
        type: "ul",
        items: [
          "Session cookies — temporary cookies that expire when you close your browser. They allow the website to link your actions during a single browsing session.",
          "Persistent cookies — remain on your device after you close your browser, for a period defined in the cookie, or until you delete them. They allow the website to remember you when you return.",
        ],
      },
    ],
  },
  {
    id: "managing-cookies",
    title: "Managing Your Cookie Preferences",
    body: [
      {
        type: "p",
        text: "You have several options to manage cookies. Most web browsers allow you to control cookies through their settings, including blocking all cookies, blocking third-party cookies, or deleting cookies that have already been set. Please note that if you block or delete cookies, some features of the website may not function properly.",
      },
      {
        type: "ul",
        items: [
          "Google Chrome — Settings > Privacy and security > Cookies and other site data.",
          "Mozilla Firefox — Settings > Privacy & Security > Cookies and Site Data.",
          "Apple Safari — Preferences > Privacy > Manage Website Data.",
          "Microsoft Edge — Settings > Cookies and site permissions > Manage and delete cookies and site data.",
        ],
      },
      {
        type: "p",
        text: "To opt out of analytics measurement offered by common providers, you can also use the opt-out tools those providers make available, such as browser add-ons for analytics opt-out.",
      },
    ],
  },
  {
    id: "do-not-track",
    title: "Do Not Track",
    body: [
      {
        type: "p",
        text: "Some browsers offer a \"Do Not Track\" (\"DNT\") signal. There is currently no widely adopted standard for how websites should respond to DNT signals, and our website does not currently respond to them. We will continue to monitor developments in this area and update this Policy if our practices change.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    body: [
      {
        type: "p",
        text: "We may update this Cookie Policy from time to time to reflect changes in the technologies we use or in applicable requirements. When we make material changes, we will update the \"Last updated\" date at the top of this page. We encourage you to review this Policy periodically.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    body: [
      {
        type: "p",
        text: "If you have questions about this Cookie Policy or our use of cookies, please contact us at privacy@wfii.ca.",
      },
    ],
  },
];

export default function CookiePolicy() {
  return (
    <LegalLayout
      title="Cookie Policy"
      lastUpdated={LAST_UPDATED}
      intro={intro}
      sections={sections}
      testId="cookie-policy-page"
    />
  );
}
